import { useEffect, useState } from 'react';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getForkSeekAuthPathWithReturn } from '@/lib/seekAuthReturn';
import { useQuery } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import {
  planForkSeekRouteWarningModeShadow,
  type LegacyRouteReadinessState,
} from '@/lib/portalIdentityEnforcementPolicy';
import seekLogo from '@/assets/logo-seek-light.svg';

type CandidateOnboardingProfile = {
  onboarding_completed: boolean | null;
  deletion_status?: string | null;
  deleted_at?: string | null;
} | null | undefined;

type ForkSeekAccountLifecycle = {
  status?: string | null;
  deletion_status?: string | null;
  deleted_at?: string | null;
  deletion_processed_at?: string | null;
  anonymized_at?: string | null;
} | null | undefined;

type ForkSeekLifecycleClient = {
  rpc: (
    fn: 'get_current_forkseek_account_lifecycle',
  ) => Promise<{ data: unknown; error: unknown }>;
};

function isDeletedForkSeekLifecycle(lifecycle: ForkSeekAccountLifecycle) {
  return lifecycle?.status === 'deleted' ||
    lifecycle?.status === 'suspended' ||
    lifecycle?.status === 'deactivated' ||
    lifecycle?.deletion_status === 'deleted' ||
    lifecycle?.deletion_status === 'anonymized' ||
    lifecycle?.deletion_status === 'deletion_requested' ||
    lifecycle?.deletion_status === 'retention_hold' ||
    lifecycle?.deletion_status === 'legal_hold' ||
    lifecycle?.deletion_status === 'suspended' ||
    lifecycle?.deletion_status === 'deactivated' ||
    Boolean(lifecycle?.deleted_at);
}

function SeekRouteLoader() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <div className="h-1.5 bg-seek/10">
          <div className="h-full w-1/3 bg-seek animate-pulse" />
        </div>
        <div className="flex items-center gap-4 p-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-seek/10">
            <img src={seekLogo} alt="" className="h-8 w-8 object-contain" />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-sm font-semibold text-foreground">{t('common.loading')}</p>
            <p className="text-xs text-muted-foreground">{t('common.pleaseWait')}</p>
            <div className="grid grid-cols-3 gap-2 pt-1">
              <span className="h-2 rounded-full bg-seek/15" />
              <span className="h-2 rounded-full bg-muted" />
              <span className="h-2 rounded-full bg-muted" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getForkSeekLegacyRouteReadiness({
  loading,
  handlingWrongPortal,
  session,
  profile,
  profileLoading,
  candidateProfile,
  pathname,
}: {
  loading: boolean;
  handlingWrongPortal: boolean;
  session: unknown;
  profile: { user_type?: string | null } | null;
  profileLoading: boolean;
  candidateProfile: CandidateOnboardingProfile;
  pathname: string;
}): LegacyRouteReadinessState {
  if (loading || handlingWrongPortal) {
    return 'legacy_loading';
  }

  if (!session) {
    return 'legacy_redirects';
  }

  if (!profile) {
    return 'legacy_loading';
  }

  if (profile.user_type !== 'candidate') {
    return 'legacy_redirects';
  }

  if (profileLoading) {
    return 'legacy_loading';
  }

  if (candidateProfile && !candidateProfile.onboarding_completed && pathname !== '/seek/onboarding') {
    return 'legacy_redirects';
  }

  return 'legacy_allows';
}

export function SeekProtectedRoute({ children }: { children: React.ReactNode }) {
  const {
    session,
    loading,
    profile,
    signOut,
    user,
    portalIdentityDiagnostic,
    portalIdentityLoading,
  } = useSeekAuth();
  const [handlingWrongPortal, setHandlingWrongPortal] = useState(false);
  const location = useLocation();

  const { data: accountLifecycle, isLoading: lifecycleLoading } = useQuery({
    queryKey: ['forkseek-account-lifecycle', user?.id],
    queryFn: async () => {
      const { data, error } = await (seekSupabase as unknown as ForkSeekLifecycleClient)
        .rpc('get_current_forkseek_account_lifecycle');
      if (error) return null;
      return data as ForkSeekAccountLifecycle;
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000,
  });

  // Check onboarding status from candidate_profiles
  const { data: candidateProfile, isLoading: profileLoading } = useQuery({
    queryKey: ['candidate-profile-onboarding', user?.id],
    queryFn: async () => {
      const { data, error } = await seekSupabase
        .from('candidate_profiles')
        .select('onboarding_completed, current_job_title, professional_summary, phone, country, deletion_status, deleted_at')
        .eq('candidate_id', user!.id)
        .single();
      if (error) return null;
      const candidateData = data as CandidateOnboardingProfile & {
        current_job_title?: string | null;
        professional_summary?: string | null;
        phone?: string | null;
        country?: string | null;
      };
      if (
        candidateData?.deletion_status === 'deleted' ||
        candidateData?.deletion_status === 'anonymized' ||
        candidateData?.deletion_status === 'deletion_requested' ||
        candidateData?.deletion_status === 'retention_hold' ||
        candidateData?.deletion_status === 'legal_hold' ||
        candidateData?.deletion_status === 'suspended' ||
        candidateData?.deletion_status === 'deactivated' ||
        candidateData?.deleted_at
      ) {
        return candidateData;
      }
      // Treat existing users with profile data as onboarding-complete
      if (!candidateData?.onboarding_completed) {
        const hasExistingData = !!(candidateData?.current_job_title || candidateData?.professional_summary || candidateData?.phone || candidateData?.country);
        if (hasExistingData) {
          // Backfill the flag so this check doesn't run again
          await seekSupabase
            .from('candidate_profiles')
            .update({ onboarding_completed: true, onboarding_step: 'existing_user' })
            .eq('candidate_id', user!.id);
          return { ...candidateData, onboarding_completed: true };
        }
      }
      return candidateData;
    },
    enabled: !!user?.id && !!profile,
    staleTime: 30 * 1000,
  });

  const forkSeekWarningModeShadowPlan = planForkSeekRouteWarningModeShadow({
    legacyRouteReadiness: getForkSeekLegacyRouteReadiness({
      loading,
      handlingWrongPortal,
      session,
      profile,
      profileLoading,
      candidateProfile,
      pathname: location.pathname,
    }),
    diagnostic: portalIdentityLoading ? null : portalIdentityDiagnostic,
  });
  void forkSeekWarningModeShadowPlan;

  useEffect(() => {
    const handleWrongPortalSession = async () => {
      if (!loading && session && profile && profile.user_type !== 'candidate') {
        setHandlingWrongPortal(true);
        await signOut();
        setHandlingWrongPortal(false);
      }
      if (!loading && session && isDeletedForkSeekLifecycle(accountLifecycle)) {
        setHandlingWrongPortal(true);
        await signOut();
        setHandlingWrongPortal(false);
      }
      if (!loading && session && candidateProfile && (
        candidateProfile.deletion_status === 'deleted' ||
        candidateProfile.deletion_status === 'anonymized' ||
        candidateProfile.deletion_status === 'deletion_requested' ||
        candidateProfile.deletion_status === 'retention_hold' ||
        candidateProfile.deletion_status === 'legal_hold' ||
        candidateProfile.deletion_status === 'suspended' ||
        candidateProfile.deletion_status === 'deactivated' ||
        candidateProfile.deleted_at
      )) {
        setHandlingWrongPortal(true);
        await signOut();
        setHandlingWrongPortal(false);
      }
    };

    handleWrongPortalSession();
  }, [loading, session, profile, signOut, candidateProfile, accountLifecycle]);

  if (loading || handlingWrongPortal) {
    return <SeekRouteLoader />;
  }

  if (!session) {
    return <Navigate to={getForkSeekAuthPathWithReturn(location)} replace />;
  }

  if (lifecycleLoading) {
    return <SeekRouteLoader />;
  }

  if (isDeletedForkSeekLifecycle(accountLifecycle)) {
    return <Navigate to={getForkSeekAuthPathWithReturn(location)} replace />;
  }

  // Wait for profile to load
  if (!profile) {
    if (location.pathname === '/seek/onboarding') {
      return <>{children}</>;
    }
    return <Navigate to="/seek/onboarding" replace />;
  }

  // Any non-candidate session should be redirected back to Seek auth
  if (profile.user_type !== 'candidate') {
    return <Navigate to={getForkSeekAuthPathWithReturn(location)} replace />;
  }

  // Wait for candidate profile onboarding check
  if (profileLoading) {
    return <SeekRouteLoader />;
  }

  // Redirect to onboarding if not completed (skip if already on onboarding page)
  if (candidateProfile && !candidateProfile.onboarding_completed && location.pathname !== '/seek/onboarding') {
    return <Navigate to="/seek/onboarding" replace />;
  }

  // Profile completion is no longer a hard navigation block.
  // Instead, job applications are gated at the action level (useSeekApply).

  return <>{children}</>;
}
